// Top-level React error boundary. Catches any uncaught render error in the
// component tree (including the entire booking flow / dashboard) and shows
// a friendly fallback with a reload action. Without this, any unhandled
// exception would white-screen the whole SPA.
//
// We also forward errors to Sentry if it's been initialized (Sentry's React
// integration prefers ErrorBoundary instrumentation over global window.onerror
// because it gets the component stack too).

import { Component } from "react";

export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // eslint-disable-next-line no-console
    console.error("[PettoBook] ErrorBoundary caught:", error, info);
    // Forward to Sentry if loaded — dynamic import keeps the bundle lean when
    // Sentry isn't configured (no DSN in env).
    import("@sentry/react").then(Sentry => {
      try { Sentry.captureException(error, { contexts: { react: { componentStack: info?.componentStack } } }); }
      catch { /* sentry not initialized — ignore */ }
    }).catch(() => { /* @sentry/react not installed yet — also fine */ });
  }

  handleReload = () => {
    try { window.location.reload(); } catch { /* noop */ }
  };

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || "Unknown error");
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        background: "radial-gradient(circle at 15% 50%, #FFE9F2, #FFD8E8 50%, #FFC4DE 100%)",
        fontFamily: "'Nunito', 'SF Pro Rounded', -apple-system, BlinkMacSystemFont, sans-serif",
      }}>
        <div style={{
          background: "rgba(255,255,255,0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 32,
          padding: "32px 28px",
          maxWidth: 420,
          width: "100%",
          textAlign: "center",
          boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
        }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>🐾</div>
          <h2 style={{ fontWeight: 900, fontSize: 22, color: "#4A4A68", margin: "0 0 6px" }}>
            出咗啲問題
          </h2>
          <p style={{ fontSize: 14, color: "#6A6A85", margin: "0 0 18px", fontWeight: 700, lineHeight: 1.5 }}>
            應用程式遇到錯誤。請重新載入再試。如果問題持續，請聯絡店家。
          </p>
          <details style={{
            textAlign: "left",
            background: "#FAFAFE",
            borderRadius: 12,
            padding: 10,
            marginBottom: 16,
            fontSize: 11,
            color: "#8A8A9E",
            border: "1px solid #F0F0F8",
          }}>
            <summary style={{ cursor: "pointer", fontWeight: 800, color: "#6A6A85" }}>技術細節</summary>
            <pre style={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              margin: "8px 0 0",
              fontFamily: "ui-monospace, SF Mono, monospace",
            }}>{msg}</pre>
          </details>
          <button
            onClick={this.handleReload}
            style={{
              width: "100%",
              padding: "14px",
              background: "linear-gradient(135deg, #FF9A9E 0%, #FF758F 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 99,
              fontSize: 15,
              fontWeight: 900,
              cursor: "pointer",
              boxShadow: "0 8px 24px rgba(255,117,143,0.4)",
            }}
          >
            重新載入
          </button>
        </div>
      </div>
    );
  }
}
